import { useDeepMemo } from '@crossnokaye/ux-common';

import { DisplayRefDisplayType, type EBIcon, type Ref } from '@repo/sdk/models/components';

import { useGetReadingsValues } from '@/features/equipment-browser/subscriptions/use-get-readings-values';
import { useGetSettingsValues } from '@/features/equipment-browser/subscriptions/use-get-settings-values';
import { dedupeRefs, extractIconRefs } from '@/helpers/extract-refs';

/**
 * Subscribes once to the union of all icon ref readings/settings for the given icons.
 * **Alarm** subscriptions stay per-`DeviceIconContent` (see `useDeviceAlarmIconConfig`) so this hook
 * only batches the work from `useGetReadingsValues` / `useGetSettingsValues` inside `DeviceIcon`.
 */
export const useSubscribeDeviceIconRefs = (icons: ReadonlyArray<EBIcon | undefined>) => {
  const { readingDisplayRefs, settingDisplayRefs } = useDeepMemo(() => {
    const reading: Ref[] = [];
    const setting: Ref[] = [];
    for (const icon of icons) {
      if (!icon) {
        continue;
      }
      const { readingRefs, settingRefs } = extractIconRefs(icon);
      reading.push(...readingRefs);
      setting.push(...settingRefs);
    }
    return {
      readingDisplayRefs: dedupeRefs(reading).map((ref) => ({
        displayType: DisplayRefDisplayType.Label,
        ref,
        label: '',
      })),
      settingDisplayRefs: dedupeRefs(setting).map((ref) => ({
        displayType: DisplayRefDisplayType.Label,
        ref,
        label: '',
      })),
    };
  }, [icons]);

  useGetReadingsValues(readingDisplayRefs);
  useGetSettingsValues(settingDisplayRefs);
};
